import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { ExtendLocales } from 'src/enums'

export const startsWithVowel = (str: string): boolean => {
  const vowels = Array.from('aeiouAEIOU')
  const firstLetter = str.normalize('NFD').split('')[0]

  return vowels.indexOf(firstLetter) !== -1
}

export const matchExtendLocale = (locale: string): ExtendLocales => {
  const formattedLocale: ExtendLocales = (locale.slice(0, -2) +
    locale.slice(-2).toUpperCase()) as ExtendLocales
  return Object.values(ExtendLocales).includes(formattedLocale)
    ? formattedLocale
    : ExtendLocales.enUS
}

export function formatPhoneNumber(phone?: string): string | undefined {
  if (!phone) return undefined
  try {
    const phoneNumber = parsePhoneNumberFromString(phone)
    if (phoneNumber && phoneNumber.isValid()) {
      return phoneNumber.formatInternational()
    }
  } catch (e) {
    /* eslint-disable-next-line no-console */
    console.error('Invalid phone number:', e)
  }
  return phone
}

export function formatMaskedPhoneNumber(input?: string): string | undefined {
  if (!input || input.length < 12) {
    return ''
  }
  const firstPart = input.substring(0, 5)
  const secondPart = input.substring(5, 8)
  const lastPart = input.substring(8)

  return `${firstPart} - ${secondPart} - ${lastPart}`
}

/*
 * util for taking a pair of query param strings and parsing them into a boolean for API requests
 *
 * @param set - the set of values to interpret as a boolean, will always return undefined if the set has more than 2 values
 * @param trueValue - the value that should be interpreted as true
 *
 * @returns boolean | undefined
 * - a set of 0 or 2 values is interpreted as undefined, since it means you want true and false values
 * - a set of... 1 value is interpreted as a boolean, based on whether it matches the trueValue
 */
export function parseBooleanQueryParamFromSet(
  set: Set<string>,
  trueValue: string,
): boolean | undefined {
  if (set.size > 2) {
    /* eslint-disable-next-line no-console */
    console.warn(
      `tried to interpret boolean for a set ${set} with more than 2 values, returning undefined`,
    )
    return undefined
  }

  if (set.size === 0 || set.size === 2) {
    return undefined
  } else {
    return set.has(trueValue)
  }
}

export function parseBooleanQueryParamFromArray(
  value: string[] | string,
  trueValue: string,
): boolean | undefined {
  const array = Array.isArray(value) ? value : [value]
  // Get unique values to check for duplicates
  const uniqueValues = new Set(array)

  if (uniqueValues.size > 2) {
    /* eslint-disable-next-line no-console */
    console.warn(
      `tried to interpret boolean for an array ${array} with more than 2 unique values, returning undefined`,
    )
    return undefined
  }

  if (array.length === 0 || uniqueValues.size === 2) {
    return undefined
  } else {
    return array.includes(trueValue)
  }
}

export function safeToStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []

  return value.filter((v) => typeof v === 'string')
}

export const convertEnumToSentenceCase = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

/**
 * Stolen and edited from `is-url-superb` library
 * https://github.com/sindresorhus/is-url-superb/blob/main/index.js
 * See https://github.com/sindresorhus/is-url-superb/tree/main for usage information
 */
export function isUrl(string: string, { lenient = false } = {}): boolean {
  if (typeof string !== 'string') {
    throw Error('Expected a string')
  }

  string = string.trim()
  if (string.includes(' ')) {
    return false
  }

  try {
    new URL(string)
    return true
  } catch {
    if (lenient) {
      return isUrl(`https://${string}`)
    }

    return false
  }
}
